// Shared helpers for the Banking feature (connections + accounts views).

import { apiErrorMessage } from './apiErrors';

/** Format a numeric balance as USD; null/undefined → '-'. */
export const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

/**
 * Format an ISO/naive date string as a locale date; empty/invalid → '-'.
 *
 * A bare `YYYY-MM-DD` is a calendar day, not an instant. `new Date()` on
 * that shape parses it as UTC midnight, which in every US zone is the
 * evening BEFORE — so an NSF deadline of 2026-09-28 rendered as 9/27
 * while the row beside it said "today" (seen 2026-09-28, env 615). Date-
 * only values are built as local dates so the day shown is the day sent.
 */
export const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const d = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3])
      )
    : new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
};

/**
 * Format a transaction amount with its debit/credit direction. Columbia sends
 * `"D"` (debit, money out) and `"C"` (credit, money in). Debits render negative.
 * `credit` is true for credits, false for debits, null when unknown — callers
 * use it to color the value.
 *
 * **Takes a decimal STRING**, which is what stored money looks like coming out
 * of the API — `NUMERIC(12,2)` rendered through the backend's one money
 * formatter. Deliberately not widened to `number | string`: pending
 * transactions are still live bank data typed as a float, and a union here
 * would make the two indistinguishable at the call site, which is how a float
 * quietly finds its way back in front of a figure someone reconciles.
 * The pending table converts explicitly instead.
 *
 * A null amount means the backend could not represent what the bank sent. It
 * renders as `-` rather than as zero, because we do not know the value and
 * must not imply one.
 */
export const formatDirectionalAmount = (
  amount?: string | null,
  direction?: string | null
): { text: string; credit: boolean | null } => {
  if (amount === null || amount === undefined) {
    return { text: '-', credit: null };
  }
  const value = Number(amount);
  if (isNaN(value)) {
    return { text: '-', credit: null };
  }
  const base = formatCurrency(Math.abs(value));
  const dir = direction?.toUpperCase();
  if (dir === 'D') return { text: `-${base}`, credit: false };
  if (dir === 'C') return { text: base, credit: true };
  return { text: base, credit: null };
};

/**
 * Bank handler errors return a structured JSON body `{ code, message }`
 * (see the backend `BankErrorBody`). `code` is a stable machine-readable
 * discriminator — e.g. `bank_unreachable` (503, transient — offer retry)
 * vs `bank_error` (502, the bank rejected the request) vs
 * `credentials_invalid` (400). Older/other endpoints may still return a
 * plain string, so fall back to that. Returns the status, a `code` when
 * present, and a human-readable `body` message.
 */
export const getBankErrorParts = (
  err: unknown
): { status?: number; code?: string; body: string } => {
  const anyErr = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  const status = anyErr?.response?.status;
  const data = anyErr?.response?.data;
  const code =
    typeof data === 'object' && data !== null
      ? (data as { code?: string }).code
      : undefined;
  // '' when the server gave no reason; callers supply their own words.
  const body = apiErrorMessage(err, '');
  return { status, code, body };
};
